import React, { useState, useEffect } from 'react';
import { lunchMenu } from '../data/menuData';
import { useTranslation } from '../hooks/useTranslation';
// import TopNavigation from './shared/TopNavigation';
import SEO from './SEO';

const NordicLunchMenu = () => {
  const { t } = useTranslation();
  const [todayKey, setTodayKey] = useState('monday');

  useEffect(() => {
    // Get current day and set it
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    const dayMap = {
      0: 'weekend', // Sunday
      1: 'monday',
      2: 'tuesday',
      3: 'wednesday',
      4: 'thursday',
      5: 'friday',
      6: 'weekend' // Saturday
    };
    
    setTodayKey(dayMap[dayOfWeek]);
  }, []);

  // Function to get date for a specific day of current week
  const getDateForDay = (dayKey) => {
    const now = new Date();
    const currentDayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Map day keys to day indices
    const dayIndexMap = {
      'monday': 1,
      'tuesday': 2,
      'wednesday': 3,
      'thursday': 4,
      'friday': 5,
      'weekend': currentDayOfWeek === 0 ? 0 : 6 // Saturday or Sunday
    };
    
    const targetDayIndex = dayIndexMap[dayKey];
    const diff = targetDayIndex - currentDayOfWeek;
    const targetDate = new Date(now);
    targetDate.setDate(now.getDate() + diff);
    
    // Format: "DEC 2"
    const month = targetDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
    const day = targetDate.getDate();
    
    return { month, day };
  };

  const days = [
    { key: 'monday', label: 'Monday', emoji: '📅' },
    { key: 'tuesday', label: 'Tuesday', emoji: '📅' },
    { key: 'wednesday', label: 'Wednesday', emoji: '📅' },
    { key: 'thursday', label: 'Thursday', emoji: '📅' },
    { key: 'friday', label: 'Friday', emoji: '📅' },
    { key: 'weekend', label: 'Weekend (Sat-Sun)', emoji: '🎉' }
  ];

  const renderMenuItem = (item, index) => (
    <div key={index} className="flex items-start gap-2 py-1">
      <span className="text-sm mt-1" style={{ color: '#C97A2B' }}>•</span>
      <div className="flex-1">
        <span className="font-medium" style={{ color: '#ffffff' }}>
          {item.name}
          {item.vegan && <span className="ml-2 text-xs font-semibold" style={{ color: 'rgb(134, 239, 172)' }}>🌱 Vegan</span>}
        </span>
        {item.description && (
          <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.6)' }}>{item.description}</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen py-8 px-4" style={{ background: '#1a1a1a' }}>
      {/* SEO */}
      <SEO 
        title="Weekly Lunch Menu | Lounas Vaasa | Lunch Vasa"
        description="Weekly signature lunch menu at Sunsari Restaurant Vaasa. Butter chicken, Nepalese specialties, vegan options. Mon-Fri 11:00-15:00, Sat 12:00-16:00. Viikon lounasmenu Vaasassa. Veckans lunchmeny i Vasa."
        keywords="lunch Vaasa, lounas Vaasa, lunch Vasa, päivän lounas, dagens lunch, butter chicken lunch, Nepalese lunch, Indian lunch, weekly menu Vaasa, lounasmenu Vaasa, lunchmeny Vasa"
      />
      
      {/* Top Navigation */}
      {/* <TopNavigation /> - moved to App.js */}
      
      <div className="max-w-5xl mx-auto pt-32">
        {/* Header with Welcome Message */}
        <div className="text-center mb-10">
          {/* Welcome Banner */}
          <div className="mb-8 p-8 rounded-3xl shadow-xl" style={{ background: 'rgba(212, 175, 55, 0.1)', border: '2px solid #C97A2B' }}>
            <h1 className="text-3xl md:text-5xl font-bold mb-4" style={{ color: '#C97A2B' }}>
              🍽️ Don't Miss Your Lunch! 🍽️
            </h1>
            <p className="text-xl md:text-2xl mb-6 font-semibold" style={{ color: '#ffffff' }}>
              Welcome to Sunsari - Where Every Meal is Made with Love
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm md:text-base">
              <span className="px-5 py-3 rounded-full font-bold shadow-md hover:scale-105 transition-transform" style={{ background: 'rgba(34, 197, 94, 0.2)', border: '2px solid rgb(34, 197, 94)', color: 'rgb(134, 239, 172)' }}>
                ✓ 100% Natural Ingredients
              </span>
              <span className="px-5 py-3 rounded-full font-bold shadow-md hover:scale-105 transition-transform" style={{ background: 'rgba(59, 130, 246, 0.2)', border: '2px solid rgb(59, 130, 246)', color: 'rgb(147, 197, 253)' }}>
                ✓ 100% Handmade
              </span>
              <span className="px-5 py-3 rounded-full font-bold shadow-md hover:scale-105 transition-transform" style={{ background: 'rgba(168, 85, 247, 0.2)', border: '2px solid rgb(168, 85, 247)', color: 'rgb(216, 180, 254)' }}>
                ✓ 100% Fresh Daily
              </span>
            </div>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: '#ffffff' }}>
            Weekly Signature Menu
            <span className="block text-xl mt-2" style={{ color: '#C97A2B' }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
          </h2>
          <p className="text-base md:text-lg mb-4" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Authentic Nepalese and Indian flavours
          </p>
          
          {/* Price Info - Compact */}
          <div className="flex flex-wrap gap-4 justify-center items-center text-sm md:text-base">
            <div className="px-6 py-3 rounded-xl shadow-md" style={{ background: 'rgba(255,255,255,0.1)', border: '2px solid rgba(255,255,255,0.2)' }}>
              <span style={{ color: 'rgba(255,255,255,0.7)' }}>Weekday: </span>
              <span className="font-bold text-lg" style={{ color: '#ffffff' }}>{lunchMenu.priceInfo.regular}</span>
              <span className="text-xs ml-2" style={{ color: 'rgba(255,255,255,0.5)' }}>(Pensioner: {lunchMenu.priceInfo.pensioner})</span>
            </div>
            <div className="px-6 py-3 rounded-xl shadow-md" style={{ background: 'rgba(212, 175, 55, 0.2)', border: '2px solid #C97A2B' }}>
              <span style={{ color: '#C97A2B' }}>Weekend: </span>
              <span className="font-bold text-lg" style={{ color: '#C97A2B' }}>{lunchMenu.priceInfo.weekend.regular}</span>
              <span className="text-xs ml-2" style={{ color: '#C97A2B', opacity: 0.8 }}>(Pensioner: {lunchMenu.priceInfo.weekend.pensioner})</span>
            </div>
          </div>
          
          {/* Sunday Special + Wolt Delivery */}
          <div className="mt-6 flex flex-wrap gap-4 justify-center">
            <div className="px-6 py-3 rounded-xl shadow-md animate-pulse" style={{ background: 'rgba(168, 85, 247, 0.2)', border: '2px solid rgb(168, 85, 247)' }}>
              <span className="font-bold" style={{ color: 'rgb(216, 180, 254)' }}>🌟 Sunday Special: À la Carte Menu</span>
            </div>
            <div className="px-6 py-3 rounded-xl shadow-md" style={{ background: 'rgba(59, 130, 246, 0.2)', border: '2px solid rgb(59, 130, 246)' }}>
              <span className="font-bold" style={{ color: 'rgb(147, 197, 253)' }}>🚚 Home Delivery via Wolt</span>
            </div>
          </div>
        </div>

        {/* All Days - Vertical List with Modern Design */}
        <div className="space-y-6">
          {days.map((day) => {
            const menu = lunchMenu.dailyMenus[day.key];
            const isToday = day.key === todayKey;
            
            return (
              <div
                key={day.key}
                className={`relative rounded-3xl shadow-lg overflow-hidden transition-all duration-300 ${
                  isToday 
                    ? 'shadow-2xl scale-[1.03]' 
                    : 'hover:shadow-xl'
                }`}
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: isToday ? '2px solid #C97A2B' : '2px solid rgba(255,255,255,0.1)'
                }}
                data-aos="fade-up"
                data-aos-delay={days.indexOf(day) * 100}
              >
                {isToday && (
                  <div className="absolute top-4 right-4 z-10">
                    <span className="px-4 py-2 font-bold rounded-full text-sm shadow-lg animate-pulse" style={{ background: '#C97A2B', color: '#1a1a1a' }}>
                      TODAY!
                    </span>
                  </div>
                )}
                {/* Day Header */}
                <div className={`px-4 sm:px-6 py-3`} style={{
                  background: isToday 
                    ? 'linear-gradient(to right, #C97A2B, #b8941f)' 
                    : day.key === 'weekend'
                    ? 'linear-gradient(to right, #6366f1, #a855f7)'
                    : 'linear-gradient(to right, #374151, #1f2937)'
                }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Calendar Date Icon */}
                      <div className="flex flex-col items-center justify-center rounded-lg p-2" style={{ 
                        background: isToday ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
                        minWidth: '60px'
                      }}>
                        <div className="text-xs font-bold" style={{ color: isToday ? '#1a1a1a' : '#ffffff', opacity: 0.9 }}>
                          {getDateForDay(day.key).month}
                        </div>
                        <div className="text-2xl font-bold leading-none" style={{ color: isToday ? '#1a1a1a' : '#ffffff' }}>
                          {getDateForDay(day.key).day}
                        </div>
                      </div>
                      
                      {/* Day Name */}
                      <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                        {day.label}
                        {menu.hours && (
                          <span className="text-sm font-normal opacity-90 ml-2">
                            {menu.hours}
                          </span>
                        )}
                      </h2>
                    </div>
                    
                    {isToday && (
                      <span className="text-xs sm:text-sm font-bold px-3 py-1 rounded-full animate-pulse" style={{ background: '#ffffff', color: isToday ? '#C97A2B' : '#1a1a1a' }}>
                        TODAY
                      </span>
                    )}
                  </div>
                </div>

                {/* Menu Content with Better Structure */}
                <div className="p-6 sm:p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-5">
                      {/* Appetizers */}
                      {menu.appetizers && menu.appetizers.length > 0 && (
                        <div className="p-4 rounded-xl transition-all" style={{ background: 'rgba(212, 175, 55, 0.1)', border: '2px solid rgba(212, 175, 55, 0.3)' }}>
                          <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#C97A2B' }}>
                            <span className="text-xl">🥗</span> APPETIZERS
                          </h3>
                          <div className="space-y-2">
                            {menu.appetizers.map((item, idx) => renderMenuItem(item, idx))}
                          </div>
                        </div>
                      )}

                      {/* Main Courses */}
                      {menu.mains && menu.mains.length > 0 && (
                        <div className="p-4 rounded-xl transition-all" style={{ background: 'rgba(255, 149, 0, 0.1)', border: '2px solid rgba(255, 149, 0, 0.3)' }}>
                          <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: 'rgb(255, 149, 0)' }}>
                            <span className="text-xl">🍛</span> MAIN COURSES
                          </h3>
                          <div className="space-y-2">
                            {menu.mains.map((item, idx) => renderMenuItem(item, idx))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right Column */}
                    <div className="space-y-5">
                      {/* Sides */}
                      {menu.sides && menu.sides.length > 0 && (
                        <div className="p-4 rounded-xl transition-all" style={{ background: 'rgba(59, 130, 246, 0.1)', border: '2px solid rgba(59, 130, 246, 0.3)' }}>
                          <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: 'rgb(96, 165, 250)' }}>
                            <span className="text-xl">🍚</span> SIDES
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {menu.sides.map((item, idx) => (
                              <span key={idx} className="text-sm px-4 py-2 rounded-full font-medium transition-all" style={{ background: 'rgba(59, 130, 246, 0.15)', border: '1px solid rgba(59, 130, 246, 0.4)', color: 'rgb(147, 197, 253)' }}>
                                {item.name} {item.vegan && '🌱'}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Desserts */}
                      {menu.desserts && menu.desserts.length > 0 && (
                        <div className="p-4 rounded-xl transition-all" style={{ background: 'rgba(168, 85, 247, 0.1)', border: '2px solid rgba(168, 85, 247, 0.3)' }}>
                          <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: 'rgb(192, 132, 252)' }}>
                            <span className="text-xl">🍰</span> DESSERTS & DRINKS
                          </h3>
                          <div className="space-y-2">
                            {menu.desserts.map((item, idx) => (
                              <div key={idx} className="flex items-center gap-2 p-1 rounded transition-all" style={{ color: 'rgba(255,255,255,0.9)' }}>
                                <span className="font-bold" style={{ color: 'rgb(192, 132, 252)' }}>✦</span>
                                <span className="font-medium">{item.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer Info */}
        <div className="mt-8 text-center">
          <p className="text-slate-600 text-sm bg-white rounded-lg px-6 py-3 inline-block shadow-sm">
            🌱 Vegan options available • 🥗 20+ fresh salads • 🍞 Fresh naan bread daily
          </p>
        </div>
      </div>
    </div>
  );
};

export default NordicLunchMenu;
